import { db, storage } from '../firebase/config';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  documentId,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';

const TOURS_COLLECTION = 'tours';

// ============ READ OPERATIONS ============

// Get all tours
export const getTours = async () => {
  try {
    const q = query(
      collection(db, TOURS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching tours:', error);
    return [];
  }
};

// Get featured tours
export const getFeaturedTours = async () => {
  try {
    const q = query(
      collection(db, TOURS_COLLECTION),
      where('featured', '==', true),
      orderBy('createdAt', 'desc'),
      limit(8)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching featured tours:', error);
    return [];
  }
};

// Get single tour by ID
export const getTourById = async (tourId) => {
  try {
    const docRef = doc(db, TOURS_COLLECTION, tourId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching tour:', error);
    return null;
  }
};

// Get tours with pagination
export const getToursPaginated = async (pageSize = 8, lastDoc = null) => {
  try {
    const base = [collection(db, TOURS_COLLECTION), orderBy('createdAt', 'desc')];
    const q = lastDoc
      ? query(...base, startAfter(lastDoc), limit(pageSize))
      : query(...base, limit(pageSize));

    const querySnapshot = await getDocs(q);
    const tours = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    return { tours, lastVisible };
  } catch (error) {
    console.error('Error fetching tours:', error);
    return { tours: [], lastVisible: null };
  }
};

// ✅ NEW: Resolve a stored filename to a token-signed Firebase Storage download URL
export const getItineraryDownloadUrl = async (fileName) => {
  if (!fileName) return '';
  if (fileName.startsWith('http')) return fileName;
  const fileRef = ref(storage, `tours/itineraries/${fileName}`);
  return await getDownloadURL(fileRef);
};

// ============ WRITE OPERATIONS ============

// Upload file helper
const uploadFile = async (file, folder, fileName) => {
  try {
    if (!file) return null;
    const storageRef = ref(storage, `${folder}/${Date.now()}_${fileName}`);
    const snapshot = await uploadBytes(storageRef, file);
    return await getDownloadURL(snapshot.ref);
  } catch (error) {
    console.error('Error uploading file:', error);
    return null;
  }
};

// Create a new tour
export const createTour = async (tourData, coverImageFile, itineraryPdfFile) => {
  try {
    const coverImageUrl = await uploadFile(
      coverImageFile,
      'tours/covers',
      coverImageFile?.name
    );
    const itineraryPdfUrl = await uploadFile(
      itineraryPdfFile,
      'tours/itineraries',
      itineraryPdfFile?.name
    );

    const docRef = await addDoc(collection(db, TOURS_COLLECTION), {
      ...tourData,
      coverImage: coverImageUrl || '',
      itineraryPdf: itineraryPdfUrl || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return {
      id: docRef.id,
      ...tourData,
      coverImage: coverImageUrl,
      itineraryPdf: itineraryPdfUrl,
    };
  } catch (error) {
    console.error('Error creating tour:', error);
    throw error;
  }
};

// Update a tour
export const updateTour = async (
  tourId,
  tourData,
  coverImageFile,
  itineraryPdfFile
) => {
  try {
    const docRef = doc(db, TOURS_COLLECTION, tourId);
    const updateData = { ...tourData, updatedAt: serverTimestamp() };

    if (coverImageFile) {
      const coverImageUrl = await uploadFile(
        coverImageFile,
        'tours/covers',
        coverImageFile.name
      );
      updateData.coverImage = coverImageUrl;
    }

    if (itineraryPdfFile) {
      const itineraryPdfUrl = await uploadFile(
        itineraryPdfFile,
        'tours/itineraries',
        itineraryPdfFile.name
      );
      updateData.itineraryPdf = itineraryPdfUrl;
    }

    await updateDoc(docRef, updateData);
    return { id: tourId, ...updateData };
  } catch (error) {
    console.error('Error updating tour:', error);
    throw error;
  }
};

// Delete a tour
export const deleteTour = async (tourId) => {
  try {
    const tourData = await getTourById(tourId);

    if (tourData?.coverImage) {
      try {
        const coverRef = ref(storage, tourData.coverImage);
        await deleteObject(coverRef);
      } catch (e) {
        console.warn('Could not delete cover image:', e);
      }
    }

    if (tourData?.itineraryPdf) {
      try {
        const pdfRef = ref(storage, tourData.itineraryPdf);
        await deleteObject(pdfRef);
      } catch (e) {
        console.warn('Could not delete itinerary PDF:', e);
      }
    }

    await deleteDoc(doc(db, TOURS_COLLECTION, tourId));
  } catch (error) {
    console.error('Error deleting tour:', error);
    throw error;
  }
};